import type { AutocompleteOption } from '@monthio/react-components'
import type { FormikProps } from 'formik'
import type { FunctionComponent } from 'react'

import { SecondaryButton } from '@monthio/design-system'
import { Add, Remove } from '@monthio/icons'
import {
	AutocompleteField,
	FormControl,
	FormGroup,
	Section,
} from '@monthio/react-components'
import { InputGroup } from '#modules/Questionnaire/Components/shared'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type { DefaultConsumerInputs } from '../../../types/questionnaire.types'
import type { Options } from '../personalInformation.types'

import { useQuestionnaireModule } from '../../../useQuestionnaireModule'
import { IdDocumentUpload } from './IdDocumentUpload'

const DsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`

export const NationalityInput: FunctionComponent<{
	formProps: FormikProps<DefaultConsumerInputs>
	listOfNationalities: Options
}> = ({ formProps, listOfNationalities }) => {
	const { values, setFieldValue } = formProps
	const [showDocumentsUpload, setShowDocumentsUpload] = useState(false)
	const { config } = useQuestionnaireModule()
	const { idDocumentNationalityExemptions } = config

	const selectedNationalities = values.personalInformation?.nationalities || []
	const nationalityInputCount = Math.max(selectedNationalities.length, 1)

	useEffect(() => {
		if (selectedNationalities.length < 1) {
			setFieldValue('personalInformation.nationalities', [''])
		}
	}, [setFieldValue, selectedNationalities.length])

	useEffect(() => {
		if (idDocumentNationalityExemptions != null) {
			const enableIdDocumentsUpload = !idDocumentNationalityExemptions.some(
				(nationalityCode) =>
					values.personalInformation?.nationalities.includes(nationalityCode),
			)
			setShowDocumentsUpload(enableIdDocumentsUpload)
		}
	}, [
		values.personalInformation?.nationalities,
		idDocumentNationalityExemptions,
	])

	const { t } = useTranslation()
	const updateNationality = (nationalities: string[]) => {
		setFieldValue('personalInformation.nationalities', nationalities)
	}

	return (
		<Section>
			<DsWrapper>
				<InputGroup
					title={
						(values.personalInformation?.nationalities.length ?? 0) > 2
							? t('consumerInputs-personalInformation-nationality-select')
							: undefined
					}
					spacing="m"
				>
					{new Array(nationalityInputCount).fill(null).map((_, i: number) => (
						<FormGroup key={i}>
							<FormControl variant="outlined">
								<AutocompleteField
									variant="outlined"
									options={listOfNationalities || []}
									onChange={(_, value) => {
										const nationalities = [
											...(values.personalInformation?.nationalities || []),
										]
										const option = value as AutocompleteOption
										nationalities[i] = option?.value.toString() || ''
										if (
											nationalities.some((nationality) =>
												idDocumentNationalityExemptions?.includes(nationality),
											)
										) {
											setFieldValue('uploadedFiles', undefined)
										}
										updateNationality(nationalities)
									}}
									value={
										listOfNationalities.find(
											(x) =>
												x.value ===
												values.personalInformation?.nationalities[i],
										) || null
									}
									label={
										t('consumerInputs-personalInformation-nationality-select') +
										(selectedNationalities.length === 2
											? ' ' + (i + 1).toString()
											: '')
									}
								/>
							</FormControl>
						</FormGroup>
					))}
					{values.personalInformation && nationalityInputCount < 2 ? (
						<StyledSecondaryButton
							onClick={() => {
								const nationalities = [
									...(values.personalInformation?.nationalities || []),
									'',
								]
								updateNationality(nationalities)
							}}
						>
							<Add height={24} />
							{t(
								'personalInformationStep-consumerInputs-addCitizenship-button',
							)}
						</StyledSecondaryButton>
					) : null}
					{nationalityInputCount === 2 && (
						<StyledSecondaryButton
							onClick={() => {
								const nationalities = [...selectedNationalities]

								nationalities.pop()
								updateNationality(nationalities)
							}}
						>
							<Remove height={24} />
							{t('btn-cancel')}
						</StyledSecondaryButton>
					)}
				</InputGroup>
				{showDocumentsUpload ? <IdDocumentUpload /> : null}
			</DsWrapper>
		</Section>
	)
}

const StyledSecondaryButton = styled(SecondaryButton)`
	flex-grow: 1;
	width: 100%;
	padding: 10px 0;
`
